import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner, Alert, ProgressBar, Form } from 'react-bootstrap';
import axios from 'axios';
import { requestWithAuthorization } from '../../network/apiRequests';

export interface BulkSelectedSession {
    id: string;
    name: string;
    start: string;
    end: string;
}

interface SessionMeta {
    sensorIds: string[];
    hasThirdPartyData: boolean;
    start?: string;
    end?: string;
    error?: string;
}

interface BulkDownloadModalProps {
    show: boolean;
    onHide: () => void;
    sessions: BulkSelectedSession[];
    useApiKey?: boolean;
}

export default function BulkDownloadModal({ show, onHide, sessions, useApiKey = false }: BulkDownloadModalProps) {
    const [metaMap, setMetaMap] = useState<Record<string, SessionMeta>>({});
    const [metaLoading, setMetaLoading] = useState(false);
    const [dataType, setDataType] = useState<'raw' | 'processed' | 'fit-sync'>('processed');
    const [apiKey, setApiKey] = useState<string>('');

    // Download progress
    const [isDownloading, setIsDownloading] = useState(false);
    const [totalFiles, setTotalFiles] = useState(0);
    const [completedFiles, setCompletedFiles] = useState(0);
    const [currentLabel, setCurrentLabel] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isDone, setIsDone] = useState(false);

    const formatIsoZ = (dStr?: string) => {
        if (!dStr) return '';
        try {
            const parsed = new Date(dStr);
            if (isNaN(parsed.getTime())) return dStr;
            return parsed.toISOString().replace(/\.\d{3}Z$/, 'Z');
        } catch (e) {
            return dStr;
        }
    };

    // Fetch session details via Bearer Auth when useApiKey is false
    const fetchMetaBearer = useCallback(async () => {
        if (sessions.length === 0) return;
        setMetaLoading(true);
        setMetaMap({});
        setError(null);
        setIsDone(false);
        setCompletedFiles(0);
        setTotalFiles(0);
        setCurrentLabel('');
        setDataType('processed');

        const results = await Promise.all(
            sessions.map(async (session) => {
                try {
                    const [recResponse, tpResponse, detailsResponse] = await Promise.all([
                        requestWithAuthorization(
                            `${process.env.REACT_APP_API_HOSTNAME}/recordings/${session.id}`
                        ),
                        requestWithAuthorization(
                            `${process.env.REACT_APP_API_HOSTNAME}/recordings/third-party-exists?recordingsId=${session.id}`
                        ),
                        requestWithAuthorization(
                            `${process.env.REACT_APP_API_HOSTNAME}/recordings/${session.id}/details`
                        ).catch(() => null)
                    ]);

                    const recData = recResponse.data;
                    let sensorIds: string[] = [];
                    if (Array.isArray(recData?.sensorId)) {
                        sensorIds = recData.sensorId;
                    } else if (typeof recData?.sensorId === 'string') {
                        sensorIds = [recData.sensorId];
                    } else if (Array.isArray(recData?.sensorIds)) {
                        sensorIds = recData.sensorIds;
                    } else if (Array.isArray(recData?.sensors)) {
                        sensorIds = recData.sensors;
                    } else if (typeof recData?.sensor_id === 'string') {
                        sensorIds = [recData.sensor_id];
                    } else if (Array.isArray(recData?.sensor_id)) {
                        sensorIds = recData.sensor_id;
                    }

                    const hasThirdPartyData: boolean =
                        tpResponse?.status === 200 && tpResponse.data?.exists === true;

                    let startTime = session.start || recData?.start || recData?.startDateTime;
                    let endTime = session.end || recData?.end || recData?.endDateTime;

                    if (detailsResponse?.status === 200 && detailsResponse.data) {
                        if (detailsResponse.data.startDateTime) startTime = detailsResponse.data.startDateTime;
                        if (detailsResponse.data.endDateTime) endTime = detailsResponse.data.endDateTime;
                    }

                    return {
                        id: session.id,
                        meta: {
                            sensorIds,
                            hasThirdPartyData,
                            start: formatIsoZ(startTime),
                            end: formatIsoZ(endTime)
                        }
                    };
                } catch (e) {
                    console.error(`Failed to fetch meta for session ${session.id}`, e);
                    return {
                        id: session.id,
                        meta: {
                            sensorIds: [],
                            hasThirdPartyData: false,
                            start: formatIsoZ(session.start),
                            end: formatIsoZ(session.end),
                            error: 'Failed to load'
                        }
                    };
                }
            })
        );

        const map: Record<string, SessionMeta> = {};
        results.forEach(({ id, meta }) => { map[id] = meta; });
        setMetaMap(map);
        setMetaLoading(false);
    }, [sessions]);

    // Reset modal state on open
    useEffect(() => {
        if (show) {
            setMetaMap({});
            setMetaLoading(false);
            setError(null);
            setIsDone(false);
            setCompletedFiles(0);
            setTotalFiles(0);
            setCurrentLabel('');
            setDataType('processed');
            if (!useApiKey) {
                fetchMetaBearer();
            }
        }
    }, [show, useApiKey, fetchMetaBearer]);

    const anyHasThirdPartyData = Object.values(metaMap).some(m => m.hasThirdPartyData);

    const handleDownload = async () => {
        setError(null);
        setIsDone(false);

        if (sessions.length === 0) {
            setError('No sessions selected.');
            return;
        }

        let activeMetaMap = metaMap;

        // If using API Key mode, fetch recordings-with-sensors via API key first
        if (useApiKey) {
            const trimmedKey = apiKey.trim();
            if (!trimmedKey) {
                setError('Please enter a valid API Key to perform downloads.');
                return;
            }

            setMetaLoading(true);
            const newMetaMap: Record<string, SessionMeta> = {};

            try {
                const validTimes: number[] = [];
                sessions.forEach(s => {
                    if (s.start) {
                        const t = new Date(s.start).getTime();
                        if (!isNaN(t)) validTimes.push(t);
                    }
                    if (s.end) {
                        const t = new Date(s.end).getTime();
                        if (!isNaN(t)) validTimes.push(t);
                    }
                });

                const minTime = validTimes.length > 0 ? Math.min(...validTimes) - (12 * 3600 * 1000) : Date.now() - (30 * 86400 * 1000);
                const maxTime = validTimes.length > 0 ? Math.max(...validTimes) + (12 * 3600 * 1000) : Date.now() + (86400 * 1000);

                const startTimeIso = formatIsoZ(new Date(minTime).toISOString());
                const endTimeIso = formatIsoZ(new Date(maxTime).toISOString());

                const recSensorsUrl = `${process.env.REACT_APP_API_HOSTNAME}/recordings-with-sensors`
                    + `?start_time=${encodeURIComponent(startTimeIso)}`
                    + `&end_time=${encodeURIComponent(endTimeIso)}`
                    + `&page_size=1000`;

                const recSensorsRes = await axios.get(recSensorsUrl, {
                    headers: { 'X-API-Key': trimmedKey }
                });

                const recordingsWithSensors: Array<{
                    recording_id: string;
                    sensors: string[];
                    start_timestamp: string;
                    end_timestamp: string;
                }> = recSensorsRes.data || [];

                for (const session of sessions) {
                    const found = recordingsWithSensors.find(r => r.recording_id === session.id);
                    if (found) {
                        newMetaMap[session.id] = {
                            sensorIds: found.sensors || [],
                            hasThirdPartyData: false,
                            start: formatIsoZ(found.start_timestamp) || formatIsoZ(session.start),
                            end: formatIsoZ(found.end_timestamp) || formatIsoZ(session.end)
                        };
                    } else {
                        newMetaMap[session.id] = {
                            sensorIds: [],
                            hasThirdPartyData: false,
                            start: formatIsoZ(session.start),
                            end: formatIsoZ(session.end),
                            error: 'Recording data or sensors not found'
                        };
                    }
                }

                setMetaMap(newMetaMap);
                activeMetaMap = newMetaMap;
            } catch (err: any) {
                console.error('Failed to fetch recordings-with-sensors:', err);
                setMetaLoading(false);
                if (err.response?.status === 401 || err.response?.status === 403) {
                    setError('Invalid or unauthorized API Key. Please check your key.');
                } else {
                    setError('Failed to retrieve recording sensor metadata with the provided API key.');
                }
                return;
            }

            setMetaLoading(false);
        }

        // Build flat list of download tasks
        const tasks: { session: BulkSelectedSession; meta: SessionMeta; sensorId: string; usesFitSync: boolean }[] = [];
        for (const session of sessions) {
            const meta = activeMetaMap[session.id];
            if (!meta || meta.sensorIds.length === 0) continue;
            for (const sensorId of meta.sensorIds) {
                const usesFitSync = dataType === 'fit-sync' && meta.hasThirdPartyData;
                tasks.push({ session, meta, sensorId, usesFitSync });
            }
        }

        if (tasks.length === 0) {
            setError(useApiKey 
                ? 'No sensor data found for the selected sessions using the provided API key.'
                : 'No downloadable sensor data found for the selected sessions.');
            return;
        }

        setTotalFiles(tasks.length);
        setCompletedFiles(0);
        setIsDownloading(true);

        try {
            for (let i = 0; i < tasks.length; i++) {
                const { session, meta, sensorId, usesFitSync } = tasks[i];
                const columnsParam = dataType;
                const label = `${session.name} — ${sensorId}`;
                setCurrentLabel(label);

                const downloadStart = meta.start || session.start;
                const downloadEnd = meta.end || session.end;

                let response: any;

                if (useApiKey) {
                    const url = `${process.env.REACT_APP_API_HOSTNAME}/download/sensor/${sensorId}`
                        + `?start=${encodeURIComponent(downloadStart)}`
                        + `&end=${encodeURIComponent(downloadEnd)}`
                        + `&format=csv&columns=${columnsParam}&isZip=true`;

                    response = await axios.get(url, {
                        headers: { 'X-API-Key': apiKey.trim() },
                        responseType: 'blob'
                    });
                } else {
                    const url = `${process.env.REACT_APP_API_HOSTNAME}/recordings/sensor/${sensorId}/download`
                        + `?start=${encodeURIComponent(downloadStart)}`
                        + `&end=${encodeURIComponent(downloadEnd)}`
                        + `&format=csv&columns=${columnsParam}&isZip=true`;

                    response = await requestWithAuthorization(url, { responseType: 'blob' }, 'get');
                }

                if (response.status === 200) {
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    const suffix = usesFitSync ? '_fit-sync' : '';
                    link.setAttribute('download', `${session.name}_${sensorId}${suffix}.zip`);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                    window.URL.revokeObjectURL(downloadUrl);
                } else {
                    setError(`Failed to download ${label}. Remaining downloads stopped.`);
                    setIsDownloading(false);
                    return;
                }

                setCompletedFiles(i + 1);
            }

            setCurrentLabel('');
            setIsDone(true);
        } catch (err: any) {
            console.error('Bulk download error:', err);
            if (useApiKey && (err.response?.status === 401 || err.response?.status === 403)) {
                setError('Invalid or unauthorized API Key. Please check your key.');
            } else {
                setError('An error occurred during download. Please verify your connection and permissions.');
            }
        } finally {
            setIsDownloading(false);
        }
    };

    const totalSensors = Object.values(metaMap).reduce((acc, m) => acc + m.sensorIds.length, 0);
    const progressPercent = totalFiles > 0 ? Math.round((completedFiles / totalFiles) * 100) : 0;

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="bg-dark text-white border-secondary">
                <Modal.Title>Download {sessions.length} Session{sessions.length !== 1 ? 's' : ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white p-4">
                {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                {isDone && <Alert variant="success" className="py-2 small">All files downloaded successfully!</Alert>}

                {metaLoading ? (
                    <div className="d-flex align-items-center gap-2 py-2">
                        <Spinner animation="border" size="sm" />
                        <span className="text-muted small">Loading session data...</span>
                    </div>
                ) : (
                    <>
                        {/* API Key Input Field (Only in API Key / Admin mode) */}
                        {useApiKey && !isDownloading && !isDone && (
                            <Form.Group controlId="bulkApiKeyInput" className="mb-4">
                                <Form.Label className="text-muted small mb-1 fw-bold text-uppercase">API Key</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder="Enter your Windpuls API key (e.g. wp_...)"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    className="bg-dark text-white border-secondary"
                                    style={{ backgroundColor: '#1a1d1f' }}
                                />
                                <Form.Text className="text-muted small">
                                    Required for sensor data download authentication across user accounts.
                                </Form.Text>
                            </Form.Group>
                        )}

                        {/* Data type selector */}
                        {!isDownloading && !isDone && (
                            <Form.Group controlId="bulkDataTypeSelect" className="mb-4">
                                <Form.Label className="text-muted small mb-2 fw-bold text-uppercase">Data Type</Form.Label>
                                <div>
                                    <Form.Check
                                        type="radio"
                                        id="bulk-data-type-processed"
                                        label="Normal Processed Data"
                                        name="bulkDataTypeOptions"
                                        checked={dataType === 'processed'}
                                        onChange={() => setDataType('processed')}
                                        className="mb-2"
                                    />
                                    {useApiKey && (
                                        <Form.Check
                                            type="radio"
                                            id="bulk-data-type-raw"
                                            label="Raw Data"
                                            name="bulkDataTypeOptions"
                                            checked={dataType === 'raw'}
                                            onChange={() => setDataType('raw')}
                                            className="mb-2"
                                        />
                                    )}
                                    {(useApiKey || anyHasThirdPartyData) && (
                                        <Form.Check
                                            type="radio"
                                            id="bulk-data-type-fit-sync"
                                            label={useApiKey ? "FIT-Synced Data" : "FIT-Synced Data (only for sessions with FIT data)"}
                                            name="bulkDataTypeOptions"
                                            checked={dataType === 'fit-sync'}
                                            onChange={() => setDataType('fit-sync')}
                                            className="mb-2"
                                        />
                                    )}
                                </div>
                            </Form.Group>
                        )}

                        {/* Session summary list */}
                        {!isDownloading && !isDone && (
                            <div className="mb-4">
                                <span className="text-muted small fw-bold text-uppercase">Selected Sessions ({sessions.length})</span>
                                <div className="mt-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                    {sessions.map(session => {
                                        const meta = metaMap[session.id];
                                        return (
                                            <div key={session.id} className="d-flex justify-content-between align-items-center py-1 border-bottom border-secondary">
                                                <span className="small">{session.name}</span>
                                                <span className="text-muted small">
                                                    {meta
                                                        ? `${meta.sensorIds.length} sensor${meta.sensorIds.length !== 1 ? 's' : ''}${meta.hasThirdPartyData ? ' · FIT' : ''}`
                                                        : (useApiKey ? 'Ready' : '—')
                                                    }
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                {totalSensors > 0 && (
                                    <div className="mt-2 text-muted small text-end">
                                        Total: {totalSensors} file{totalSensors !== 1 ? 's' : ''} to download
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Progress section */}
                        {(isDownloading || isDone) && totalFiles > 0 && (
                            <div className="mb-4">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="small text-muted fw-bold text-uppercase">Progress</span>
                                    <span className="small text-muted">{completedFiles} / {totalFiles} files</span>
                                </div>
                                <ProgressBar
                                    now={progressPercent}
                                    label={`${progressPercent}%`}
                                    variant="primary"
                                    animated={isDownloading}
                                    style={{ height: '10px', borderRadius: '5px' }}
                                />
                                {currentLabel && (
                                    <div className="mt-2 text-muted small text-truncate">
                                        Downloading: <em>{currentLabel}</em>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-dark border-secondary">
                {isDone ? (
                    <Button variant="secondary" onClick={onHide}>Close</Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={onHide} disabled={isDownloading || metaLoading}>Cancel</Button>
                        <Button
                            variant="primary"
                            onClick={handleDownload}
                            disabled={
                                isDownloading || 
                                metaLoading || 
                                (useApiKey ? !apiKey.trim() : totalSensors === 0)
                            }
                        >
                            {isDownloading || metaLoading ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Processing...</>
                            ) : (
                                'Start Download'
                            )}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
